<template>
  <div class="mb-5">
    <b-row>
      <b-col
        lg="7"
        xl="5"
      >
        <b-alert
          :show="phrasesWithMoreThanThreeWordsLength > 0"
          variant="sm alert-warning"
          data-test="phrases-with-more-than-three-words-alert"
        >
          Phrases can't exceed more than three words
        </b-alert>
        <b-alert
          :show="bannedWordsLength > 0"
          variant="sm alert-warning"
          data-test="banned-words-alert"
        >
          <div v-if="bannedWordsLength === 1">
            The word <span class="text-danger">{{ bannedWords[0] }}</span> can't be excluded
          </div>
          <div v-else>
            The words <span class="text-danger">{{ bannedWords.join(", ") }}</span> can't be excluded
          </div>
        </b-alert>
      </b-col>
    </b-row>
    <div class="col col-lg-10 col-xl-8 pl-0">
      <div class="brand-safety-keywords-title">
        <div class="d-inline">
          <base-help-tooltip>
            <template slot="left">
              <h3
                data-test="brand-safety-label"
              >
                Brand Safety Keywords
              </h3>
            </template>
            <template slot="popover-body">
              <p>
                We will apply our industry brand protection using Natural Language Processing
                to prevent your ads appearing against content in any of the following categories:
              </p>
              <p class="bg-light p-3 rounded">
                abortion, air-crash, banking-economic-and-financial-crime, child-mistreatment,
                crime, cyber-threat, danger-and-death, demonstrations,
                diseases-and-syndromes, drug-addiction, drunk-and-disorderly,
                extreme-right-and-nationalist-parties, financial-crisis, fire, fraud,
                industrial-accidents, job-loss,
                manipulation-of-people, natural-disasters, prejudicial-discrimination,
                sexual, terrorism, torture,
                unrest-conflicts-and-war
              </p>
              <p>
                Words separated by a space are treated as a phrase (3 consecutive words maximum).
                Use a comma (,) to separate individual keywords or word pairs.
                Words such as ‘and’, ‘but’ or ‘if’ are prohibited from being added.
              </p>
              <p>
                You may supplement our brand protection by excluding up to 20 words or word pairs.
                So for example, you might add green,
                in which case if the word green appears in the content of an article, your ad will
                never be appear there. Alternatively, you might want to be more specific and choose
                'green frog' to only exclude stories which include the consecutive words green and frog.
              </p>
            </template>
          </base-help-tooltip>
        </div>
        <div class="d-inline">
          <b-btn
            class="btn btn-secondary mr-1 btn-sm"
            data-test="campaign-clear-blocked-button"
            @click="clearBlockedKeyWords"
            :disabled="blockedWords.length < 1"
          >
            Clear Blocked
          </b-btn>
          <b-btn
            class="btn btn-secondary ml-1 btn-sm"
            data-test="save-changes-button"
            @click="clearAllKeywords"
            :disabled="keywords.length < 1 && blockedWords.length < 1"
          >
            Clear List
          </b-btn>
        </div>
      </div>
    </div>
    <b-row>
      <b-col
        lg="10"
        xl="8"
      >
        <b-form-group
          id="fieldset-1"
          label-for="input-1"
        >
          <b-input-group>
            <b-form-input
              id="input-keyword"
              v-model="keywordInput"
              autocomplete="off"
              trim
              @paste.prevent="onPasteKeyword"
              @keyup.enter.native="processAddKeywordInput"
              data-test="campaign-keyword"
              :disabled="GET_IS_CAMPAIGN_READONLY"
              :readonly="GET_IS_CAMPAIGN_READONLY"
            />
            <b-input-group-append>
              <b-button
                variant="option"
                :disabled="disabledAddKeyword"
                @click="processAddKeywordInput"
                data-test="campaign-add-keyword"
              >
                Exclude
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col>
        <span
          class="badge badge-default"
          :class="{ 'bg-light': keywords.length === maxKeywords }"
        >
          {{ keywords.length }} / {{ maxKeywords }}
        </span>
      </b-col>
      <b-col cols="12">
        <div
          class="tags mt-2 mb-1"
          data-test="blocked-tags"
          v-if="blockedWords.length > 0"
        >
          <span
            class="tag tag-primary tag-danger"
            v-for="(item, key) in blockedWords"
            :key="key"
            :id="`tooltip-target-${key}`"
          >
            {{ item }}
            <a
              href="javascript:;"
              class="tag-addon"
              @click="onDeleteBlockedKeyword(key)"
              :disabled="GET_IS_CAMPAIGN_READONLY"
            >
              <i class="fa-light fa-xmark" />
            </a>
          </span>
        </div>
      </b-col>
      <b-col cols="12">
        <div
          class="tags mt-2 mb-5"
          data-test="keyword-tags"
          v-if="keywords.length > 0"
        >
          <span
            class="tag tag-primary"
            v-for="(item, key) in keywords"
            :key="key"
            :id="`tooltip-target-${key}`"
          >
            {{ item }}
            <a
              href="javascript:;"
              class="tag-addon"
              @click="onDeleteKeyword(key)"
              :disabled="GET_IS_CAMPAIGN_READONLY"
            >
              <i class="fa-light fa-xmark" />
            </a>
          </span>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import helper from '@/core/helper';

export default {
  name: 'BrandSafety',
  components: {
    BaseHelpTooltip,
  },
  data() {
    return {
      keywordInput: '',
      keywords: [],
      bannedWords: [],
      phrasesWithMoreThanThreeWords: [],
      maxBrandSafetyPhraseLength: 255,
      maxKeywords: 999,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'linkingWords',
      'blacklist',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    blockedWords() {
      return this.bannedWords.concat(this.phrasesWithMoreThanThreeWords);
    },
    bannedWordsLength() {
      return this.bannedWords.length;
    },
    phrasesWithMoreThanThreeWordsLength() {
      return this.phrasesWithMoreThanThreeWords.length;
    },
    hasIncludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.inclusions.length !== 0);
    },
    hasExcludeTargeting() {
      return (this.geoTargeting.countryInclusionsExclusions.exclusions.length !== 0);
    },
    disabledAddKeyword() {
      return this.keywords.length === this.maxKeywords || this.keywordInput.length === 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.PATCH_BLACKLIST,
    ]),
    onPasteKeyword(event) {
      this.addKeywordInput(event.clipboardData.getData('text'));
    },
    processAddKeywordInput() {
      if (this.keywordInput.length === 0) return;
      this.addKeywordInput(this.keywordInput);
      this.keywordInput = '';
    },
    addKeywordInput(input) {
      if (this.keywords.length === this.maxKeywords) return;
      const keywordsList = this.getFormattedBrandSafetyKeyWordsList(input);
      keywordsList.filter((keyword) => !this.isKeywordAlreadyPresent(keyword))
        .forEach((keyword) => {
          // eslint-disable-next-line no-nested-ternary
          const destinationArray = this.hasMoreThanThreeKeywords(keyword) ? this.phrasesWithMoreThanThreeWords
            : this.isBannedWord(keyword) ? this.bannedWords : this.keywords;
          destinationArray.push(keyword);
        });
      if (this.keywords.length > this.maxKeywords) this.keywords = this.keywords.slice(0, this.maxKeywords);
      this.processChangedBlacklistActions();
    },
    isKeywordAlreadyPresent(keyword) {
      return helper.includesValueInArrayIgnoringCase(this.keywords, keyword)
        || helper.includesValueInArrayIgnoringCase(this.bannedWords, keyword)
        || helper.includesValueInArrayIgnoringCase(this.phrasesWithMoreThanThreeWords, keyword);
    },
    processChangedBlacklistActions() {
      this.$emit('updateKeyWordsValid', this.bannedWords.length === 0 && this.phrasesWithMoreThanThreeWords.length === 0);
      this.$emit('keywordsUpdated');
    },
    clearAllKeywords() {
      this.keywords = [];
      this.blacklist.blacklistKeywords = this.keywords;
      this.clearBlockedKeyWords();
      this.processChangedBlacklistActions();
    },
    clearBlockedKeyWords() {
      this.bannedWords = [];
      this.phrasesWithMoreThanThreeWords = [];
      this.blacklist.bannedWords = this.bannedWords;
      this.blacklist.phrasesWithMoreThanThreeWords = this.phrasesWithMoreThanThreeWords;
      this.processChangedBlacklistActions();
    },
    onDeleteBlockedKeyword(index) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        const bannedWordsLength = this.bannedWords.length;
        if (index < bannedWordsLength) {
          this.bannedWords.splice(index, 1);
        } else {
          this.phrasesWithMoreThanThreeWords.splice(index - bannedWordsLength, 1);
        }
        this.processChangedBlacklistActions();
      }
    },
    onDeleteKeyword(index) {
      if (!this.GET_IS_CAMPAIGN_READONLY) {
        this.keywords.splice(index, 1);
        this.processChangedBlacklistActions();
      }
    },
    isBannedWord(word) {
      if (word.trim().split(' ') > 1) return false;
      return this.linkingWords.includes(word);
    },
    hasMoreThanThreeKeywords(phrase) {
      return phrase.trim().split(/\s+/).length > 3;
    },
    populatePageForExistingBlacklist() {
      this.keywords = this.blacklist.blacklistKeywords ?? [];
      if (!this.blacklist.bannedWords) {
        this.blacklist.bannedWords = [];
      }
      if (!this.blacklist.phrasesWithMoreThanThreeWords) {
        this.blacklist.phrasesWithMoreThanThreeWords = [];
      }
      this.bannedWords = this.blacklist.bannedWords;
      this.phrasesWithMoreThanThreeWords = this.blacklist.phrasesWithMoreThanThreeWords;
    },
    getFormattedBrandSafetyKeyWordsList(input) {
      const keywordsList = input.split(/\r?\n|,/)
        .map((keyword) => keyword.trim())
        .filter((trimmedKeyword) => trimmedKeyword !== '');
      return this.updatePhrasesToMaxLength(keywordsList);
    },
    updatePhrasesToMaxLength(phrases) {
      const updatedPhrases = [];
      phrases.forEach((phrase) => {
        if (phrase.length > this.maxBrandSafetyPhraseLength) {
          updatedPhrases.push(phrase.substring(0, this.maxBrandSafetyPhraseLength));
        } else {
          updatedPhrases.push(phrase);
        }
      });
      return [...new Set(updatedPhrases)];
    },
    saveBlacklist() {
      return this.PATCH_BLACKLIST(this.keywords);
    },
  },
  async mounted() {
    this.populatePageForExistingBlacklist();
    this.$emit('updateKeyWordsValid', this.bannedWords.length === 0 && this.phrasesWithMoreThanThreeWords.length === 0);
  },
};
</script>
<style lang="scss" scoped>
.brand-safety-keywords-title {
  display: flex;
  justify-content: space-between;
}
</style>
